/**
 * Payload Migrations - ProjectContentPayload.schemaVersion transitions.
 *
 * Registers all version steps for the ProjectContentPayload format.
 * See storageAdapter.ts for the current payload definition.
 *
 * v1 (baseline):
 *   {
 *     projectId, userId, pages, blocks, schema, workflows,
 *     designSystem, settings, files, sequenceNum, schemaVersion, savedAt
 *   }
 *
 * v2 (Phase 2 - canonical persistence):
 *   v1 + canonicalSchema?: NuvraSchema
 *
 * ADDING A FUTURE MIGRATION:
 *   1. Define the new/removed/renamed fields
 *   2. Add a migration step: register(SCOPE_PAYLOAD, { fromVersion: N, toVersion: N+1, ... })
 *   3. Bump CURRENT_SCHEMA_VERSION in storageAdapter.ts
 *   4. Add test coverage in phase2Persistence.test.ts
 *
 * SAFETY RULES FOR ALL PAYLOAD MIGRATIONS:
 *   - New required fields: provide safe defaults (empty array, empty object, 0)
 *   - Removed fields: preserve as passthrough (do not delete - cloud may still have them)
 *   - Renamed fields: copy old → new, keep old as passthrough for backward compat
 *   - Never infer values from other fields (no data fabrication)
 */

import { migrationRegistry, SCOPE_PAYLOAD } from "./migrationRegistry";

/** Current payload schema version - must match CURRENT_SCHEMA_VERSION in storageAdapter.ts. */
export const CURRENT_PAYLOAD_VERSION = 2;

// ─── v1 → v2 ───────────────────────────────────────────────────────────────
//
// Adds the optional canonicalSchema field.
// canonicalSchema is NOT populated here - it is never inferred from flat fields.
// The bridge populates it on the next save via canonicalProvider.
// Flat fields (pages, blocks, schema, workflows, …) are preserved unchanged.

migrationRegistry.register(SCOPE_PAYLOAD, {
  fromVersion: 1,
  toVersion:   2,
  description: "Add canonicalSchema field to ProjectContentPayload (Phase 2 - canonical persistence)",
  migrate(data: Record<string, unknown>): Record<string, unknown> {
    return {
      ...data,
      schemaVersion:   2,
      canonicalSchema: undefined, // populated by bridge on next save - never fabricated
    };
  },
  validate(result: unknown): boolean {
    return (
      typeof result === "object" &&
      result !== null &&
      (result as Record<string, unknown>).schemaVersion === 2
    );
  },
});

/**
 * Returns true if the given payload version needs migration to the current version.
 * Used by LocalAdapter.read before returning loaded data.
 */
export function payloadNeedsMigration(schemaVersion: number | undefined): boolean {
  const v = schemaVersion ?? 0;
  return v < CURRENT_PAYLOAD_VERSION;
}
